<template>
  <section v-if="canSolve || canReview" class="card-actions" :class="{ 'card-actions--review': mode === 'review' }">
    <template v-if="canSolve">
      <label v-show="solutionExpanded" class="action-label" :for="`solution-${issue.issueId}`">{{ $t("解决说明") }}</label>
      <el-input :id="`solution-${issue.issueId}`" v-model="description" type="textarea"
        :rows="solutionExpanded ? 3 : 1" maxlength="1024" :show-word-limit="solutionExpanded" :disabled="submitting"
        :resize="solutionExpanded ? 'vertical' : 'none'" :aria-label="$t('解决说明')"
        :placeholder="$t('写下解决方法和结果，或添加图片…')" @focus="solutionExpanded = true" />
      <el-upload v-show="solutionExpanded" class="inline-upload" action="" accept="image/*" list-type="picture-card"
        :class="{ 'inline-upload--full': files.length >= 4 }"
        :file-list="files" :limit="4" multiple
        :disabled="submitting || uploading > 0"
        :before-upload="validateImage" :http-request="uploadImage"
        :on-change="syncFiles" :on-remove="syncFiles" :before-remove="beforeRemove"
        :on-exceed="onExceed" :on-preview="previewImage">
        <i class="el-icon-plus" aria-hidden="true"></i>
        <span class="upload-label">{{ $t("添加图片") }}</span>
      </el-upload>
      <div v-show="solutionExpanded" class="action-row">
        <span class="action-hint">{{ uploading ? $t("图片上传中…") : $t("文字或图片至少一项 · 最多 4 张") }}</span>
        <el-button type="primary" size="small" :loading="submitting" :disabled="uploading > 0 || completed"
          @click="submitSolution">{{ $t("提交解决") }}</el-button>
      </div>
    </template>
    <template v-else-if="canReview">
      <div class="action-row">
        <span class="action-label">{{ $t("验收方案") }}</span>
        <div class="review-buttons">
          <el-button size="small" :disabled="submitting || completed || locked" @click="rejecting = !rejecting">
            {{ rejecting ? $t("取消") : $t("不通过") }}
          </el-button>
          <el-button v-if="!rejecting" size="small" type="primary" :loading="submitting" :disabled="completed || locked"
            @click="submitApproval(3)">{{ $t("通过") }}</el-button>
        </div>
      </div>
      <div v-if="rejecting" class="rejection-form">
        <label class="action-label" :for="`reason-${issue.issueId}-${solution.answerId}`">{{ $t("不通过原因") }}</label>
        <el-input :id="`reason-${issue.issueId}-${solution.answerId}`" v-model="reason" type="textarea" :rows="3"
          maxlength="255" show-word-limit :disabled="submitting || locked" :placeholder="$t('请说明需要改进的地方…')" />
        <div class="action-row rejection-submit">
          <span class="action-hint">{{ $t("提交后退回待解决") }}</span>
          <el-button size="small" type="danger" plain :loading="submitting" :disabled="completed || locked"
            @click="submitApproval(1)">{{ $t("提交不通过") }}</el-button>
        </div>
      </div>
    </template>
    <el-dialog :visible.sync="previewVisible" append-to-body :title="$t('图片预览')" width="720px">
      <img :src="previewUrl" :alt="$t('解决图片')" class="upload-preview" />
    </el-dialog>
  </section>
</template>

<script>
import { t } from "@/i18n";
import axios from 'axios';
import app from '@/App';

export default {
  name: 'IssueCardActions',
  props: {
    issue: { type: Object, required: true },
    solution: { type: Object, default: null },
    user: { type: Object, required: true },
    canApprove: Boolean,
    mode: { type: String, default: 'solve', validator: value => ['solve', 'review'].includes(value) },
    locked: Boolean,
  },
  data() {
    return {
      solutionExpanded: false,
      description: '', files: [], uploading: 0, submitting: false, completed: false,
      rejecting: false, reason: '', previewVisible: false, previewUrl: '',
    };
  },
  computed: {
    canSolve() { return this.mode === 'solve' && this.issue.issueStatus === 0; },
    canReview() {
      return this.mode === 'review' && this.canApprove && this.issue.issueStatus === 1 && this.solution
        && this.solution.result === 0 && Boolean(this.solution.answerId);
    },
  },
  watch: {
    'issue.issueStatus'() { this.resetDraft(); },
    'solution.answerId'() { this.reason = ''; this.rejecting = false; this.completed = false; },
  },
  methods: {
    resetDraft() {
      this.solutionExpanded = false;
      this.description = '';
      this.files = [];
      this.reason = '';
      this.rejecting = false;
      this.completed = false;
    },
    syncFiles(file, files) { this.files = files; },
    validateImage(file) {
      if (!file.type || !file.type.startsWith('image/')) {
        this.$message.warning(t("请选择图片文件"));
        return false;
      }
      return true;
    },
    beforeRemove(file) {
      // Invalid files must still be removable when another file is uploading.
      if (file.raw && !file.raw.type.startsWith('image/')) return true;
      return !this.submitting && this.uploading === 0;
    },
    onExceed() { this.$message.warning(t("最多只能上传4张图片")); },
    previewImage(file) { this.previewUrl = file.url; this.previewVisible = true; },
    uploadImage({ file, onSuccess, onError }) {
      this.uploading += 1;
      app.methods.uploadFile(file).then(url => {
        const item = this.files.find(entry => entry.uid === file.uid);
        if (item) {
          if (item.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
          item.url = url;
          onSuccess({ url });
        }
      }).catch(error => {
        onError(error);
        this.$message.error(t("图片上传失败，请重试"));
      }).finally(() => { this.uploading -= 1; });
    },
    async submitSolution() {
      if (this.submitting || this.completed || !this.canSolve) return;
      if (this.uploading || this.files.some(file => file.status !== 'success')) {
        this.$message.warning(t("请等待图片上传完成"));
        return;
      }
      const description = this.description.trim();
      const images = this.files.map(file => file.response && file.response.url).filter(Boolean);
      if (!description && !images.length) {
        this.$message.warning(t("请填写解决说明或上传图片"));
        return;
      }
      this.submitting = true;
      try {
        await axios.post(`${app.url}/issues/${this.issue.issueId}/solutions`, {
          description, images, solverId: this.user.userId,
        });
        this.completed = true;
        this.$emit('changed', { status: 1, solution: { description, images, solver: this.user, result: 0 } });
        this.$message.success(t("已提交解决方案，等待验收"));
      } catch (error) {
        this.$message.error(error?.response?.data?.message || t("提交失败，请重试"));
      } finally { this.submitting = false; }
    },
    async submitApproval(result) {
      if (this.submitting || this.completed || this.locked || !this.canReview || ![1, 3].includes(result)) return;
      const reason = this.reason.trim();
      if (result === 1 && !reason) {
        this.$message.warning(t("请填写不通过原因"));
        return;
      }
      const solutionId = this.solution.answerId;
      this.submitting = true;
      this.$emit('busy', true);
      try {
        await axios.put(`${app.url}/solutions/${solutionId}/approval`, {
          approvalResult: result, rejectionReason: result === 1 ? reason : '', rating: null,
        });
        this.completed = true;
        this.$emit('changed', { status: result === 3 ? 2 : 0, solutionId, result, reason: result === 1 ? reason : '' });
        this.$message.success(result === 3 ? t("验收通过") : t("已退回待解决"));
      } catch (error) {
        this.$message.error(error?.response?.data?.message || t("验收失败，请重试"));
      } finally {
        this.submitting = false;
        this.$emit('busy', false);
      }
    },
  },
};
</script>

<style scoped>
.card-actions { padding: 14px 16px; border-top: 1px solid var(--border-light); }
.card-actions.card-actions--review { margin-top: 12px; padding: 0; border: 0; }
.action-label { display: block; margin-bottom: 8px; color: var(--text-regular); font-size: 13px; font-weight: 600; }
.action-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.action-row .action-label { margin: 0; }
.action-hint { color: var(--text-muted); font-size: 12px; }
.review-buttons { display: flex; gap: 8px; }
.review-buttons .el-button { margin: 0; }
.rejection-form { margin-top: 12px; }
.rejection-submit { margin-top: 10px; }
.inline-upload { margin: 10px 0; }
.inline-upload ::v-deep .el-upload--picture-card,
.inline-upload ::v-deep .el-upload-list__item { width: 68px; height: 68px; margin: 0 8px 8px 0; border-radius: 8px; }
.inline-upload ::v-deep .el-upload--picture-card { line-height: 1; display: inline-flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; background: var(--surface-hover); border-color: var(--border-color); }
.inline-upload ::v-deep .el-upload--picture-card i { font-size: 20px; color: var(--text-muted); }
.inline-upload--full ::v-deep .el-upload--picture-card { display: none; }
.upload-label { font-size: 11px; color: var(--text-muted); }
.upload-preview { display: block; width: 100%; height: auto; }
@media (max-width: 768px) { .card-actions { padding: 14px; } }
</style>
